// src/components/language/LanguageSelector.tsx
import { useState, useCallback, useRef, useEffect } from 'react';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '@/config/routes';
import { useLocalization } from '@/hooks/useLocalization';
import { ChevronDown } from 'lucide-react';

const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentLang, changeLanguage } = useLocalization();

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleLanguageChange = useCallback((newLang: SupportedLanguage) => {
    changeLanguage(newLang);
    setIsOpen(false);
  }, [changeLanguage]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-background/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
        aria-label="Select Language"
      >
        <span>{SUPPORTED_LANGUAGES[currentLang].nativeName}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 py-2 w-48 bg-background rounded-lg shadow-lg border border-border/10 backdrop-blur-sm z-50">
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, { nativeName, name }]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code as SupportedLanguage)}
              className={`w-full text-left px-4 py-2 hover:bg-primary/10 focus:outline-none focus:bg-primary/10 transition-colors ${
                code === currentLang ? 'font-semibold bg-primary/5' : ''
              }`}
            >
              <span className="block text-sm">{nativeName}</span>
              {code !== currentLang && (
                <span className="block text-xs text-muted-foreground">{name}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;